import { cloneDeep } from "lodash";
import React from "react";
import * as MaterialDesignIcons from "react-icons/md";
import { GoTriangleDown } from "react-icons/go";
import { Link, useLocation } from "react-router-dom";
import IFamily from "../models/IFamily";

import { ELCID, IMediaPlanPage, TBreakpoint } from "../models/IMediaToolkit";
import { MobileMenu } from "./MobileMenu";
import AAPService from "../services/AAPService";
import { ITranslations } from "../data/strings";

export interface ICtaMapProps {
    pages: IMediaPlanPage[];
    family: IFamily;
    curBreakpoint: TBreakpoint;
    lcid: ELCID;
    translations: ITranslations;
}

// these should be odd
export const breakpointCtaShowMap: { [breakpoint in TBreakpoint]: number } = {
    xs: 3,
    sm: 3,
    md: 7,
    lg: 9,
    xl: 13,
    "2xl": 99,
};

export const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

export const CtaMap: React.FunctionComponent<ICtaMapProps> = (props) => {
    const [navOpen, setNavOpen] = React.useState<boolean>(false);

    const location = useLocation();
    const numTilesToShow = breakpointCtaShowMap[props.curBreakpoint];

    let curPageIdx = 0;
    let curValidPageIdx = 0;

    for (let i = 0; i < props.pages.length; i++) {
        if (location.pathname === props.pages[i].navigateTo) {
            curPageIdx = i;
            break;
        }
    }

    // get current valid step
    const validPages = props.pages.filter(
        (page) => (!page.availablePriority && !page.dontCountAsStep) || props.family.priorities.indexOf(page.spoId) > -1
    );
    for (let i = 0; i < validPages.length; i++) {
        if (props.pages[curPageIdx].identifier === validPages[i].identifier) {
            curValidPageIdx = i;
            break;
        }
    }

    // figure out renderable range
    const renderableStepsToGoAroundCurrent = Math.floor(numTilesToShow / 2);
    let firstRenderableTileIndex = clamp(curPageIdx - renderableStepsToGoAroundCurrent, 0, props.pages.length - 1);
    let lastRenderableTileIndex = clamp(curPageIdx + renderableStepsToGoAroundCurrent, 0, props.pages.length - 1);
    let tilesBefore = curPageIdx - firstRenderableTileIndex;
    let tilesAfter = lastRenderableTileIndex - curPageIdx;
    let unrenderedTilesBefore = renderableStepsToGoAroundCurrent - tilesBefore;
    let unrenderedTilesAfter = renderableStepsToGoAroundCurrent - tilesAfter;

    const renderableSteps = cloneDeep(props.pages).slice(
        firstRenderableTileIndex - unrenderedTilesAfter,
        lastRenderableTileIndex + 1 + unrenderedTilesBefore
    );
    const mobileMode = props.pages.length > renderableSteps.length;

    return (
        <div className={`bg-aap-cream-500 flex justify-center py-5 px-5 xl:px-0 print:hidden`}>
            {mobileMode ? (
                <div className="burger">
                    <button type="button" className="text-aap-red-500" onClick={() => setNavOpen(true)} id={`btn_mobileMenuOpen_${ELCID[props.lcid]}`}>
                        <MaterialDesignIcons.MdMenu size={48} />
                    </button>
                </div>
            ) : null}
            {renderableSteps.map((page, renderableIdx) => {
                let pageIdx = props.pages.map((propPage) => propPage.identifier).indexOf(page.identifier);

                const pageIsPriority =
                    !page.availablePriority ||
                    (page.availablePriority && props.family.priorities.indexOf(page.spoId) > -1);
                let stepCompleted = false;
                let currentStep = false;

                let glyphClasses = "bg-aap-gray-200 text-aap-gray-500 w-10 h-10";
                let textClasses = "text-aap-gray-500";
                let lineLeftClasses = "bg-aap-gray-200";
                let lineRightClasses = "bg-aap-gray-200";

                if (pageIdx < curPageIdx) {
                    // already completed steps
                    stepCompleted = true;
                    glyphClasses = "bg-aap-green-500 text-white w-10 h-10";
                    textClasses = "text-aap-green-500";
                    lineLeftClasses =
                        unrenderedTilesBefore === 0 && renderableIdx === 0
                            ? `bg-gradient-to-r from-aap-cream-500 to-aap-green-500`
                            : "bg-aap-green-500";
                    lineRightClasses = "bg-aap-green-500";

                    // for non-priority steps
                    if (!pageIsPriority) {
                        glyphClasses = "bg-aap-green-300 text-white shadow-aap-gray-300 w-10 h-10";
                        textClasses = "text-aap-green-300";
                    }
                } else if (curPageIdx === pageIdx) {
                    // current step
                    currentStep = true;
                    glyphClasses = "bg-aap-red-500 text-white w-14 h-14 -mt-2";
                    textClasses = "text-aap-red-500";
                    lineLeftClasses = "bg-gradient-to-r from-aap-green-500 to-aap-red-500";
                    lineRightClasses = "bg-gradient-to-r from-aap-red-500 to-aap-gray-200";
                } else {
                    // future step
                    // defaults already defined above

                    if (unrenderedTilesAfter === 0 && renderableIdx === renderableSteps.length - 1) {
                        lineRightClasses = `bg-gradient-to-r from-aap-gray-200 to-aap-cream-500`;
                    }

                    // for non-priority steps
                    if (!pageIsPriority) {
                        glyphClasses =
                            "bg-aap-cream-500 text-aap-gray-300 shadow-none border border-aap-gray-300 w-10 h-10";
                        textClasses = "text-aap-gray-300";
                    }
                }

                return (
                    <div
                        style={{ width: `${100 / renderableSteps.length}%` }}
                        className={`first-of-type:pl-0 last-of-type:pr-0 relative`}
                        key={`ctamap-item-${page.identifier}`}
                    >
                        {pageIdx > 0 ? (
                            <div
                                className={`w-1/2 h-1 absolute top-5 left-0 z-10 transition-all duration-200 ${lineLeftClasses}`}
                            ></div>
                        ) : null}
                        <Link to={page.navigateTo} className="block text-center relative z-20" id={`btn_ctaMapNode_${page.identifier}_${ELCID[props.lcid]}`}>
                            <div
                                className={`mx-auto rounded-full relative shadow-md shadow-aap-gray-400 transition-all duration-200 ${glyphClasses}`}
                            >
                                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                    {React.createElement(AAPService.GetMaterialIcon(page.icon || "help_outline"), {
                                        size: currentStep ? 36 : 28,
                                    })}
                                </div>
                                {stepCompleted && pageIsPriority ? (
                                    <div className="absolute left-1 -bottom-1 rounded-full bg-aap-green-700 text-white p-0.5">
                                        <MaterialDesignIcons.MdCheck size={10} />
                                    </div>
                                ) : null}
                            </div>
                            <div className={`text-xs pt-2 px-2 text-center transition-all duration-200 ${textClasses}`}>
                                {page.title}
                            </div>
                        </Link>
                        {pageIdx < props.pages.length - 1 ? (
                            <div
                                className={`w-1/2 h-1 absolute top-5 right-0 z-10 transition-all duration-200 ${lineRightClasses}`}
                            ></div>
                        ) : null}
                    </div>
                );
            })}
            {mobileMode ? (
                <>
                    <button
                        type="button"
                        onClick={() => setNavOpen(true)}
                        className="stepCount block w-24 text-xs text-center mt-2 text-aap-gray-400 h-max"
                        id={`btn_ctaMapRemainingPages_${ELCID[props.lcid]}`}
                    >
                        <div>
                            {curValidPageIdx + 1}/{validPages.length}
                        </div>
                        <div>{props.translations.remaining}</div>
                        <GoTriangleDown className="mx-auto" />
                    </button>
                    <MobileMenu
                        family={props.family}
                        curPageIdx={curPageIdx}
                        pages={props.pages}
                        navOpen={navOpen}
                        setNavOpen={setNavOpen}
                        lcid={props.lcid}
                        translations={props.translations}
                    />
                </>
            ) : null}
        </div>
    );
};
